import { ComponentProps, ReactNode, useContext, useState } from 'react';
import { useAuth } from '@frontegg/react';

import Card from '~/shared/components/Card';
import { Volume } from '~/shared/types/api';
import Tag from '~/shared/components/Tag';
import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/Buttons';
import DeleteIcon from '~/shared/assets/icons/delete.svg';

import OptimisticSwitch from '../FleetManagerPage/OptimisticSwitch';
import { statusToTagVariantMap } from '../common/helpers';
import { DatafySwitchContext, DatafySwitchVolume } from '../DatafySwitchProvider';
import { tableColumns } from '../common/tableColumns';

import DeleteModal from './DeleteModal';

export default function GeneralInfo({ volume }: { volume: Volume }) {
  const { datafySwitchVolumes } = useContext(DatafySwitchContext);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { user } = useAuth();
  const isUserReadOnly = !!user?.roles?.find((r) => r.key === 'ReadOnly');

  return (
    <Card>
      <div className="h-[48px] flex justify-between items-center mb-[20px]">
        <Typography variant="H5">General Info</Typography>

        <div className="gap-[35px] items-center flex">
          {volume.monitoring === 'Unattached' && !isUserReadOnly ? (
            <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
              <DeleteIcon className="w-6 aspect-square fill-primary-400" />
              <Typography variant="buttons-1" color="text-primary-500">
                Delete
              </Typography>
            </Button>
          ) : null}

          <OptimisticSwitch volume={volume} showLabel />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-x-[6px] gap-y-[30px]">
        {getInfoItems(volume, datafySwitchVolumes).map((item) => (
          <InfoItem key={item.title} {...item} />
        ))}
      </div>

      <DeleteModal showModal={isModalOpen} volume={volume} onClose={() => setIsModalOpen(false)} />
    </Card>
  );
}

function InfoItem({ title, value }: { title: string; value: ReactNode }) {
  return (
    <div className="px-[8px] pt-[10px] pb-[4px] flex flex-col w-full justify-between h-[70px] max-w-full">
      <Typography variant="p3" truncate title={title}>
        {title}
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? <Value value={value} /> : value}
    </div>
  );
}

function Value({
  variant = 'default',
  value,
}: {
  variant?: 'default' | 'green';
  value?: string | number;
}) {
  return (
    <Typography
      variant="p2"
      color={
        !value || value === 'N/A'
          ? 'text-grey-1'
          : variant === 'green'
            ? 'text-lime-green'
            : undefined
      }
      truncate
      title={value?.toString()}
    >
      {value || 'N/A'}
    </Typography>
  );
}

function getInfoItems(
  volume: Volume,
  datafySwitchVolumes: DatafySwitchVolume[]
): ComponentProps<typeof InfoItem>[] {
  const { monitoring, statusProgress, volumeId } = volume;

  const machineVolume = datafySwitchVolumes.find((v) => v.volumeId === volumeId);

  const progress = !machineVolume ? statusProgress : undefined;

  const optimisticValue: Volume['monitoring'] = !machineVolume
    ? monitoring
    : machineVolume?.state === 'datafying' && monitoring === 'Monitored'
      ? 'Migrating'
      : machineVolume?.state === 'datafying' && monitoring === 'Migrating'
        ? 'Monitored'
        : machineVolume?.state === 'undatafying' && monitoring === 'Managed'
          ? 'Unmigrating'
          : machineVolume?.state === 'undatafying' && monitoring === 'Migrating'
            ? 'Monitored'
            : monitoring;

  return tableColumns.map(({ key, title, getValue }): ComponentProps<typeof InfoItem> => {
    const defaultItems = { title, value: getValue ? getValue(volume) : (volume[key] as string) };

    if (key === 'monitoring') {
      return {
        ...defaultItems,
        value: (
          <Tag
            text={optimisticValue}
            variant={statusToTagVariantMap[optimisticValue]}
            progress={progress}
          />
        ),
      };
    }
    return defaultItems;
  });
}
